<template>
	<div class="mt-3">
		<div v-if="isTypeCustomer && !hasPotentialDates" class="font-weight-bold">
			Bitte schlage neue Termine vor:
		</div>
		<div v-else class="font-weight-bold">Termin wählen:</div>
		<template v-if="hasPotentialDates">
			<div
				v-for="potentialDate in appointment.potentialDates"
				:key="potentialDate.id"
			>
				<b-form-checkbox
					:checked="chosenDate === potentialDate"
					:disabled="datesNotPossible || isDatePastDue(potentialDate)"
					@change="onChosenDateChanged($event, potentialDate)"
				>
					<span :class="{'text-line-through': datesNotPossible || isDatePastDue(potentialDate)}">
						{{ potentialDate.timeStamp | fullDateTime }}
					</span>
				</b-form-checkbox>
			</div>
		</template>
		<div class="mt-3">
			<template v-if="hasPotentialDates && !allDatesPastDue">
				<b-form-checkbox v-model="datesNotPossible" @change="onDatesNotPossibleChanged">
					Ich kann an keinem dieser Termine
				</b-form-checkbox>
			</template>
			<div v-if="datesNotPossible || !hasPotentialDates || allDatesPastDue" class="mt-3">
				<JonInfo v-if="isTypeApplicant && allDatesPastDue" class="mb-3">
					<template #message>
						Du hast alle Terminvorschläge versäumt, die an dich gesendet wurden. Sende jetzt
						Terminvorschläge an den Arbeitgeber oder teile mit, dass du nach wir vor interessiert bist und
						neue Terminvorschläge möchtest.
					</template>
				</JonInfo>
				<RadioGroup
					v-if="isTypeApplicant"
					v-model="counterProposalType"
					:options="counterProposalTypeOptions"
					class="mt-3"
					@input="emitChangeEvent"
				/>
				<FormGroup v-if="isCounterProposalTypeSuggestions" class="mt-3">
					<PotentialDates
						v-model="counterProposalDates"
						:max-time="counterProposalMaxTime"
						@input="emitChangeEvent"
					/>
				</FormGroup>
			</div>
		</div>
	</div>
</template>

<script>
import FormGroup from "@/vue/common/form/Group";
import ArrowLink from "@/vue/common/ArrowLink";
import RadioGroup from "@/vue/common/form/RadioGroup";
import InfoBox from "@/vue/common/InfoBox";
import {isDefined} from "@/helper/Value";
import {createFromString} from "@/helper/util/Time";
import Button from "@/vue/common/Button";
import PotentialDates from "@/vue/communication/conversation/message/reply/PotentialDates.vue";
import JonInfo from "@/vue/common/JonInfo.vue";
import {DateTime} from "luxon";
import {arrayIsNotEmpty} from "@/helper/Array";

const COUNTER_PROPOSAL_TYPE_SUGGESTIONS = 'suggestions';
const COUNTER_PROPOSAL_TYPE_CUSTOMER = 'customer';

const TYPE_APPLICANT = 'applicant';
const TYPE_CUSTOMER = 'customer';

export default {

	components: {
		JonInfo,
		PotentialDates,
		Button,
		FormGroup,
		ArrowLink,
		InfoBox,
		RadioGroup,
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		appointment: {
			type: Object,
			required: true,
		},
		type: {
			type: String,
			required: true,
		},
	},

	data ()
	{
		const suggestionsText = this.allDatesPastDue
			? 'Ich will neue Termin-Vorschläge machen'
			: 'Ich will Termin-Gegenvorschläge machen';

		const customerText = this.allDatesPastDue
			? 'Ich will bitte neue Termin-Vorschläge erhalten'
			: 'Ich möchte bitte neue Terminvorschläge erhalten';

		return {
			chosenDate: this.value?.chosenDate,
			datesNotPossible: this.value?.datesNotPossible || false,
			counterProposalType: this.type === TYPE_CUSTOMER
				? COUNTER_PROPOSAL_TYPE_SUGGESTIONS
				: this.value?.counterProposalType,
			counterProposalTypeOptions: Object.freeze([
				{value: COUNTER_PROPOSAL_TYPE_SUGGESTIONS, text: suggestionsText},
				{value: COUNTER_PROPOSAL_TYPE_CUSTOMER, text: customerText},
			]),
			counterProposalDates: this.value?.counterProposalDates || [],
		};
	},

	computed: {
		isTypeApplicant ()
		{
			return this.type === TYPE_APPLICANT;
		},
		isTypeCustomer ()
		{
			return this.type === TYPE_CUSTOMER;
		},
		counterProposalMaxTime ()
		{
			if (this.isTypeCustomer)
			{
				return undefined;
			}

			if (!this.appointment || !this.appointment.counterProposalMaxTime)
			{
				return undefined;
			}

			return createFromString(this.appointment.counterProposalMaxTime);
		},
		isCounterProposalTypeSuggestions ()
		{
			return this.counterProposalType === COUNTER_PROPOSAL_TYPE_SUGGESTIONS;
		},
		hasPotentialDates ()
		{
			return this.appointment.potentialDates.length > 0;
		},
		allDatesPastDue ()
		{
			if (!this.hasPotentialDates)
			{
				return false;
			}

			const potentialDates = this.appointment.potentialDates;

			return potentialDates.length === potentialDates.filter(date => this.isDatePastDue(date)).length;
		},
		chosenDateValid ()
		{
			if (!this.hasPotentialDates)
			{
				return true;
			}

			return this.datesNotPossible || isDefined(this.chosenDate);
		},
		counterProposalDatesValid ()
		{
			if (!this.isCounterProposalTypeSuggestions)
			{
				return true;
			}

			return isDefined(this.counterProposalDates) && arrayIsNotEmpty(this.counterProposalDates);
		},
		dataValid ()
		{
			if (this.chosenDate)
			{
				return true;
			}

			if (!this.hasPotentialDates)
			{
				return this.counterProposalDatesValid;
			}

			if (this.datesNotPossible || this.allDatesPastDue)
			{
				return isDefined(this.counterProposalType)
					&& (
						this.counterProposalType === COUNTER_PROPOSAL_TYPE_CUSTOMER
						|| this.counterProposalDatesValid
					);
			}

			return false;
		},
	},

	methods: {
		isDatePastDue (potentialDate)
		{
			const timeStamp = DateTime.fromISO(potentialDate.timeStamp);

			return DateTime.now() > timeStamp;
		},
		onDatesNotPossibleChanged (checked)
		{
			if (checked)
			{
				this.chosenDate = undefined;
			}

			this.emitChangeEvent();
		},
		onChosenDateChanged (checked, date)
		{
			if (!checked)
			{
				this.chosenDate = undefined;

				this.emitChangeEvent();

				return;
			}

			this.chosenDate = date;

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.dataValid)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('input', {
				chosenDate: this.datesNotPossible
					? undefined
					: this.chosenDate,
				datesNotPossible: this.datesNotPossible,
				allDatesPastDue: this.allDatesPastDue,
				counterProposalType: this.counterProposalType,
				counterProposalDates: (this.datesNotPossible || !this.hasPotentialDates || this.allDatesPastDue) && this.isCounterProposalTypeSuggestions
					? this.counterProposalDates
					: undefined,
			});
		}
	}
}
</script>
